import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * App-level error boundary. Catches render-time errors that would otherwise
 * unmount the React tree. Shows a Kerbly-branded fallback.
 *
 * Note: React error boundaries do NOT catch:
 *   - Errors inside event handlers (those bubble up normally — wrap with try/catch)
 *   - Async errors (use Promise.catch)
 *   - SSR errors
 *   - Errors thrown by the boundary itself
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };
  private readonly reloadFlagKey = 'kerbly:chunk-reload-attempted';

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);

    // When a deploy lands while someone is mid-session, lazy-loaded chunk URLs
    // can change and the old runtime may fail to import a now-missing file.
    // One hard reload usually picks up the fresh manifest/chunks.
    const message = String(error?.message ?? '');
    const isChunkLoadError =
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Importing a module script failed') ||
      message.includes('Loading chunk');
    if (!isChunkLoadError) return;

    try {
      if (sessionStorage.getItem(this.reloadFlagKey) === '1') return;
      sessionStorage.setItem(this.reloadFlagKey, '1');
      window.location.reload();
    } catch {
      // Ignore storage access issues and fall back to the standard UI.
    }
  }

  handleReload = (): void => {
    try {
      sessionStorage.removeItem(this.reloadFlagKey);
    } catch {
      // Ignore storage access issues.
    }
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen bg-navy flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-navy-2 border border-navy-3 rounded-lg p-8 text-center">
          <div className="text-coral text-4xl mb-3">●</div>
          <h1 className="font-display text-2xl text-foam mb-2">
            Something came out in the wash.
          </h1>
          <p className="text-mist text-sm mb-6">
            Kerbly hit an unexpected error. Refreshing the page usually sorts it. If it keeps
            happening, drop us a line at <span className="text-foam">hello@kerbly.app</span>.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="bg-green hover:bg-green-dark text-navy font-medium rounded-pill px-6 h-11 transition-all"
          >
            Reload
          </button>
          {import.meta.env.DEV ? (
            <details className="mt-6 text-left">
              <summary className="text-mist text-xs cursor-pointer hover:text-foam">
                Error details (dev only)
              </summary>
              <pre className="mt-2 text-xs text-coral whitespace-pre-wrap break-words font-mono">
                {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
              </pre>
            </details>
          ) : null}
        </div>
      </div>
    );
  }
}
